/* Purpose: All of a client's data can be viewed on one unified page. 
 *          All uploaded files will be availabel for download in a zip folder.
 *          This Review page can either be accessed by a client (to view his own data) or by an admin.
 *          (Behaviour slightly changes when being viewd by client vs admin)
 * Props: ClientID (str)
 *        credentials (map)
 *        AdminID (str) --optional
 *        adminUsername (str) --optional
 *        clientInfo (map) --optional
 *        allLoans (list) --optional
 *        setAllLoans (func) --optional
 *        allClients (list) --optional
 *        setAllClients (setState) --optional
 *        updateLoanReviews (func) --optional
 *        updateLoanStatus (func) --optional
 * Author: R. Markowitz (April 2025)
 * Dev Notes: 
*/

import React, {useState, useEffect} from "react";
import JSZip from 'jszip';
import {saveAs} from 'file-saver';
import PageTitle from "../../Components/PageTitle";
import PortalNavBar from  "../../Layout/PortalNavBar"
import {Container, Button, Row, Col, Form, Stack, ProgressBar} from 'react-bootstrap';
import SubmitButton from '../../Components/Button';
import * as ClientRequData from '../../Data/RequiredClientData' ; 
import * as LoanRequData from "../../Data/RequiredLoanData";
import { Link } from "react-router-dom";
import ConfirmStatusUpdate from "../../Components/ConfirmStatusUpdate";
import NoteModal from "../../Components/NoteModal";
import AdminUploadModal from "../../Components/AdminUploadModal";

const Review = (props) => {
    const ClientID = props.ClientID;
    const AdminID = props.AdminID || null;
    const adminUsername = props.adminUsername || null;
    const clientInfo = props.clientInfo || null;
    const credentials = props.credentials; 
    const allLoans = props.allLoans;
    const setAllLoans = props.setAllLoans?props.setAllLoans:(() => {console.log("This is not an Admin account.")})
    const updateLoanReviews = props.updateLoanReviews || (() => {console.log("This is not an Admin account.")})
    const updateLoanStatus = props.updateLoanStatus || (() => {console.log("This is not an Admin account.")})
    const [givenName, setGivenName] = useState("Loading...");
    const [familyName, setFamilyName] = useState("");
    const [LoanID, setLoanID] = useState();
    const [loanList, setLoanList] = useState([{"LoanID": "Loading..."}]);
    const personalRequInfo = ClientRequData.personalInfo;
    const financialRequInfo = ClientRequData.financialInfo;
    const [loanRequInfo, setLoanRequInfo] = useState();
    const [loanRequFileList, setLoanRequFileList] = useState();
    const [loanFiles, setLoanFiles] = useState();
    const [personalInfo, setPersonalInfo] = useState({"Loading...": ""});
    const [financialInfo, setFinancialInfo] = useState({"Loading...": ""});
    const [loadedLoans, setLoadedLoans] = useState({})
    const [loanInfo, setLoanInfo] = useState({"Loading...": ""});
    const [filePathList, setFilePathList] = useState([]);
    const [missingFiles, setMissingFiles] = useState([]);
    const [loanBasicInfo, setLoanBasicInfo] = useState({});
    const [clickedLoan, setClickedLoan] = useState(false);
    const [loanInfoLoaded, setLoanInfoLoaded] = useState();
    const [filesToZip, setFilesToZip] = useState([]);
    const [zipReady, setZipReady] = useState();
    const [readyForDownload, setReadyForDownload] = useState();
    const onlyIfOccupied = ["Lease Agreement", "Lease Verification", "Rent Verification", "Security Deposit Receipt"];
    const [confirmStatusUpdate, setConfirmStatusUpdate] = useState(false);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // Scroll to top of page
    useEffect(() =>{
        window.scrollTo({top: 0, left: 0, behavior: 'smooth' });
    }, [])

    // Load all the personal and financial info. Save any personal files (ValidID) into filePathList
    useEffect(() => {
        const getGeneralInfo = async () => {
            try {
                const response = await fetch(`https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/getGeneralInfo`, {
                    "method": "POST",
                    "body":JSON.stringify({
                        "ClientID": ClientID,
                        "credentials": credentials
                    })
                });
                if (!response.ok) {
                    console.log("Error loading Personal and Financial info. Response not ok.");
                    alert("Something went wrong while trying to retrieve the the general information for this client. Please try refreshing the page.")
                }else{
                    const general = await response.json();
                    const loans = await getLoanList();
                    const data = {...general,
                        "Loans": [...loans]
                    }

                    setGeneralInfoVars(data)
                }                  
            } catch (error) {
                console.log("Something went wrong when trying to load the clients personal and financial data. Error: ", error)
                alert("Something went wrong while trying to retrieve the the general information for this client. Please try refreshing the page.")
            }
        }

        const getLoanList = async () => {
            try {
                const response = await fetch(`https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/getLoanList?ClientID=${encodeURIComponent(ClientID)}`);
                if (!response.ok) {
                    console.log("Error loading loan list. Response not ok.");
                    return([{"LoanID": "Something went wrong when tring to access your current loans"}]);
                }else{
                    const data = await response.json();
                    if (data && !data.length == 0) {
                           return data                         
                    } else {
                        console.log("Data empty.");
                        return([{"LoanID": "No Loans on File."}]);   
                    }
                }
            } catch (error) {
                return([{"LoanID": "Something went wrong when tring to access your current loans"}]);
            }
        }

        const setGeneralInfoVars = (data) => {
            if (data && !data == []) {
                // Set all the personal info to personalInfo and all the personal files to filePathList or missingFiles
                let tempPersonalInfo = {}; 
                let tempMissingFiles = [...missingFiles];
                let tempFileList = [...filePathList];

                //Set tempPersonalInfo with the values in data according to the order of personalRequInfo
                personalRequInfo.map((field) => {
                    if (field.fieldType == "file"){
                        if (!data["Personal"][field.fieldName] || data["Personal"][field.fieldName] == ""){
                            tempMissingFiles.push(field.fieldName);
                        }else{
                            tempFileList.push(data["Personal"][field.fieldName]);
                        }

                    }else{
                        Object.assign(tempPersonalInfo, {[field.fieldName]:data["Personal"][field.fieldName]});
                        if (field.fieldName == "First Name"){
                            setGivenName(data["Personal"][field.fieldName])
                        }else if (field.fieldName == "Last Name"){
                            setFamilyName(data["Personal"][field.fieldName])
                        }
                        
                    }
                })                                  
                setMissingFiles(tempMissingFiles);
                setFilePathList(tempFileList);
                setPersonalInfo(tempPersonalInfo);
                
                // Set financial info
                let tempFinancialInfo = {};
                financialRequInfo.map((field) => {
                    Object.assign(tempFinancialInfo, {[field.fieldName]:data["Financial"][field.fieldName]})
                })
                setFinancialInfo(tempFinancialInfo);

                //set the loan list
                if (data['Loans'].length > 0){
                    setLoanList(data["Loans"])
                }else{
                    setLoanList([{"LoanID": "No Loans on File."}])
                }
                
            } else {
                console.log("Data empty.");
                alert("Something went wrong while trying to retrieve the the general information for this client. Please try refreshing the page.")
            }
        }
        if (ClientID && credentials && !clientInfo ){
            getGeneralInfo();  
        }else if (clientInfo){
            setGeneralInfoVars(clientInfo)
        }
       
    }, [ClientID, credentials]);

    // Load all the loan info of clicked loan. 
    // Save any loan files into filePathList and any missing file to missingFiles
    useEffect(() => {
        const getLoanInfo = async () => {
            try {
                const response = await fetch(`https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/getLoanInfo`, {
                    "method": "POST",
                    "body":JSON.stringify({
                        "ClientID": ClientID,
                        "LoanID": LoanID,
                        "credentials": credentials
                    })
                });
                if (!response.ok) {
                    console.log("Error loading Loan info and files. Response not ok.");
                    alert("Something went wrong while trying to retrieve the loan information and files. Please try refreshing the page.")
                }else{
                    const data = await response.json();

                    if (data && !data == []) {
                            let isOccupied;
                            if (data.LoanType == "Refinance"){
                                isOccupied = data.LoanInfo['Occupancy Status'] == "Vacant"?false:true
                            }else{
                                isOccupied = false
                            } 
                            
                            // Add the loan files to the file list
                            let tempFileList = [...filePathList];
                            let tempMissingFiles = missingFiles;

                            Object.keys(data["LoanFiles"]).forEach((fileName) => {
                                if ((isOccupied || !onlyIfOccupied.includes(fileName)) ){
                                    if(data["LoanFiles"][fileName] == "" && !tempMissingFiles.includes(fileName)){
                                        tempMissingFiles = [...tempMissingFiles, fileName];
                                    }else if (data["LoanFiles"][fileName] !== "" && !tempFileList.includes(fileName)){
                                        tempFileList = [...tempFileList, ...data["LoanFiles"][fileName]]
                                    }    
                                }
                            })

                            tempFileList.sort((a,b) => {if (a < b){return -1}else if (a > b){return 0}});
                            tempMissingFiles.sort((a,b) => {if (a < b){return -1}else if (a > b){return 0}});
                            setFilePathList(tempFileList);
                            setMissingFiles(tempMissingFiles)

                            // If the user is an admin, set the loanRequFileList with data so it can be used to view individual files
                            let tempLoanFiles = loanRequFileList.map((field) => (
                                {...field, "value": data['LoanFiles'][field.fileName] || []}
                            ))
                            setLoanRequFileList(tempLoanFiles)
                            setLoanFiles(data['LoanFiles'])

                            // Set the Loan Info
                            let tempLoanInfo = {};

                            loanRequInfo.map((field) => {
                                Object.assign(tempLoanInfo, {[field.fieldName]:data["LoanInfo"][field.fieldName]})
                            })
                            setLoanInfo(tempLoanInfo);

                    } else {
                        console.log("Data empty.");
                        alert("Something went wrong while trying to retrieve the loan information and files. Please try refreshing the page.")

                    }
                }
            } catch (error) {
                console.log("Something went wrong when trying to load the clients loan data. Error: ", error)
                alert("Something went wrong while trying to retrieve the loan information and files. " + error + " Please try refreshing the page.")
            }
        }

        if (clickedLoan){
            // If loan is already cached in loadedLoans, pull from there, if loan was not loaded yet, fetch from aws
            if (loadedLoans[LoanID]){
                setFilePathList(loadedLoans[LoanID]["FilePathList"]);
                setMissingFiles(loadedLoans[LoanID]["MissingFiles"]);
                setLoanInfo(loadedLoans[LoanID]["LoanInfo"]);
            }else{
               getLoanInfo(); 
            }
                        
        }
    }, [clickedLoan, LoanID, loanRequInfo, credentials])

    // When a loan is clicked, set clickedLoan to trigger getLoanInfo and set LoanID
    const handleLoanClick = (loan) => {
        if (loan.LoanType == "Refinance"){
            setLoanRequInfo(LoanRequData.refiInfo)
            setLoanRequFileList(LoanRequData.refiFiles)
        }else if (loan.LoanType == "Purchase"){
            setLoanRequInfo(LoanRequData.purchaseInfo)
            setLoanRequFileList(LoanRequData.purchaseFiles)
        }else if (loan.LoanType == "Fix and Flip"){
            setLoanRequInfo(LoanRequData.fixAndFlipInfo)
            setLoanRequFileList(LoanRequData.fixAndFlipFiles)
        }
        if(AdminID){
            loan = allLoans.find((l) => l.LoanID == loan.LoanID)
            if(loan.isNew){
                updateLoanReviews(ClientID, loan.LoanID)
            }
        }
        setLoanBasicInfo({...loan, ClientID: ClientID, ClientName: givenName + " " + familyName})
        setClickedLoan(true);
        setLoanID(loan.LoanID);
    };

    // When "View a different loan" is clicked, set LoanID to "" and set
    const handleBackToLoanList = () => {
        setClickedLoan(false);
        setLoanID("");
        setLoanInfo({"Loading...":""});
        setLoanBasicInfo({});

        //Resetting filePathList and missingFiles to take out any loan files, 
        // if more general files than just valid id need to edit to include additional files
        if (missingFiles.includes("Valid ID")){
            setFilePathList([]);
            setMissingFiles(["Valid ID"]);
        }else if (filePathList && filePathList.length != 0){
            let tempFilePathList = [...filePathList]
            tempFilePathList = tempFilePathList.filter((file) => file.includes("/General_"))
            setFilePathList(tempFilePathList)
            setMissingFiles([])
        }
    }


    // When the loan info loads set loan info loaded so the data can be displayed and loaded loans cache 
    useEffect(() => {
        if (!loanInfo["Loading..."]){
            setLoanInfoLoaded(true);
            setLoadedLoans({
                ...loadedLoans, 
                [LoanID]:{"LoanInfo" : loanInfo, "FilePathList": filePathList, "MissingFiles": missingFiles}
            })  
        }
        
    }, [loanInfo])

    // When the user clicks the "Download all files" button, get all the files from S3
    // Download them in a zip folder
    const handleDownloadZip = async () => {
        const fetchFile = async (filePath) => {
          try {
            //const brokenFilePath = filePath.split("_")
            //const fileName = brokenFilePath[brokenFilePath.length -2] + "_" + brokenFilePath[brokenFilePath.length -1]
            console.log("Getting presigned url...");
            const response = await fetch('https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/downloadFileFromS3', {
                "method": "POST",
                "body": JSON.stringify({
                    "filePath": filePath,
                    "credentials": credentials
                })
            });
            
            if (response.ok){
                const presignedData = await  response.json();
                const fetchFileResponse = await handleFetchFromS3(presignedData, filePath);
            }else{
                console.log("Something went wrong when trying to get a presigned url. response not ok.");
            }
            
          } catch (error) {
            console.error('Failed to fetch file:', error);
          }
        }

        const handleFetchFromS3 = async (urlData, fileName) => {   
            fileName = fileName.split("/")[1]
            try{      
                const response = await fetch(urlData);
                if (response.ok){
                    const file = await response.arrayBuffer();
                    let tempFilesToZip = [...filesToZip];
                    tempFilesToZip = prevFiles =>[...prevFiles, {fileName, content:file}];
                    setFilesToZip(tempFilesToZip);
                }else{
                    console.log("response not ok")
                }
                
            }catch (error){
                console.log("Something went wrong when trying to fetch file.");
                console.log("Error: ", error);
                return error;
            }
        }
        
        if (filePathList.length != 0){
            setZipReady(false)
            for (const filePath of filePathList){ 
                await fetchFile(filePath);
            }
            setZipReady(true);
        }else{
            alert("There are no files to download");
        }
        
    };

    // When the zip is ready, set ready for download
    useEffect(() => {
        const isFilesToZipUpdated = ((prevState) => prevState !== filesToZip);

        if (filesToZip.length > 0 && isFilesToZipUpdated && zipReady){
          setReadyForDownload(true);  
        }   
    }, [zipReady, filesToZip])

    // When the files to Zip is all ready, zip and download the files
    useEffect(() => {
        const downloadZip = () => {
            const zip = new JSZip();

            // Loop through the files and add them to the zip
            
            filesToZip.forEach(file => {
                zip.file(file.fileName, file.content);
            });

            // Make the zip name based on the client id and loan id
            let zipName = givenName + "-" + familyName + "_" ;
            zipName += !LoanID || LoanID == ""?("General_"):(LoanID + "_");
            zipName += "Files.zip";

            // Generate the ZIP file and trigger download
            zip.generateAsync({type: "blob"}).then(content => {
                saveAs(content, zipName);
                setZipReady(false);
                setReadyForDownload(false);
            }).catch(err => {
                console.error("Failed to generate ZIP ", err);
                alert("Failed to generate zip")
                setZipReady(false);
                setReadyForDownload(false);
            });
        };

        if (readyForDownload && filesToZip.length > 0){
            downloadZip();
            setFilesToZip([]);
            setZipReady(false);
            setReadyForDownload(false);
        }
    }, [readyForDownload])

    return(
        <div>
            {!AdminID && (<PortalNavBar ClientID = {ClientID}/>)}

            <PageTitle pageName = {"Review - " + givenName + " "  + familyName} />
            
            <Container className = 'mx-3'>
                <Row xs = {1} sm = {1} md = {2}>
                    <Col><h2><u><strong>Personal Information</strong></u></h2></Col>
                    {!AdminID && (
                        <Col><Link className="mt-auto" to = "/Portal/General"><SubmitButton text = "Edit General Info" width = '40'/></Link></Col>
                    )}
                </Row>
                

                <Container>
                    {Object.keys(personalInfo).map((fieldName, index) => (
                        <Row key = {index}>
                            <Col>
                                <p><strong>{fieldName}:</strong> </p>
                            </Col>
                            <Col>
                                <p>{personalInfo[fieldName]}</p>
                            </Col>
                        </Row>
                        
                    ))}
                </Container>

            </Container>

            <Container className = 'mx-3 mb-3'>
                <Row >
                    <Col><h2><u><strong>Financial Information</strong></u></h2></Col>
                </Row>
                
                <Container>
                    {Object.keys(financialInfo).map((fieldName, index) => (
                        <Row key = {index}>
                            <Col>
                                <p><strong>{fieldName}:</strong> </p>
                            </Col>
                            <Col>
                                <p>{financialInfo[fieldName]}</p>
                            </Col>
                        </Row>
                        
                    ))}
                </Container>

            </Container>

            {!clickedLoan && (
            <Container className = 'mx-3'>
                <h2><u><strong>Choose a loan</strong></u></h2>

                <Container>
                    {loanList.map((loan, index) => (
                        <Row key = {index}>
                            {loan.LoanID === "No Loans on File."? (
                                <p><strong>{loan.LoanID}</strong></p>
                            ):(
                                <Row>
                                    <Col xs="auto">
                                        <Form.Check 
                                            key = {index}
                                            type = "radio" 
                                            name = {loan.LoanID}
                                            id = {loan.LoanID} 
                                            label = {loan.LoanID}
                                            value = {loan.LoanID}
                                            onChange = {() => {handleLoanClick(loan)}}
                                        />
                                    </Col>

                                    {loan.Status && (
                                        <Col>
                                            <ProgressBar label = {loan.Status}>
                                                <ProgressBar variant="light" now={25} key={1} label = "Intake"/>
                                                <ProgressBar variant="secondary" now={loan.Status != "Intake"?25:0} key={2} label = {loan.Status == "Processing"? loan.Status: "Processed"}/>
                                                <ProgressBar variant="info" now={(loan.Status == "Underwriting" || loan.Status == "Approved")?25:0} key={3} label = {loan.Status == "Underwriting"? loan.Status: "Underwritten"}/>
                                                <ProgressBar variant="success" now={loan.Status == "Approved"?25:0} key={4} label = {loan.Status == "Approved"? loan.Status: "Approved"}/>
                                            </ProgressBar>
                                        </Col>
                                    )}
                                </Row>
                            )}
                            
                        </Row>                       
                    ))}
                </Container>

            </Container>
            )}

            {/* After a loan is clicked, dispaly the loan info */}
            {clickedLoan && loanInfoLoaded && (
            <Container className = 'mx-3'>
                <Row xs={1} md={3} lg={3}>
                    <Col xs={12} md={6} lg={6}>
                        <h2><u><strong>Loan Information - {LoanID}</strong></u></h2>
                        
                        {loanBasicInfo.Status && (<Col>
                            <ProgressBar label = {loanBasicInfo.Status}>
                                <ProgressBar variant="light" now={25} key={1} label = "Intake"/>
                                <ProgressBar variant="secondary" now={loanBasicInfo.Status != "Intake"?25:0} key={2} label = {loanBasicInfo.Status == "Processing"? loanBasicInfo.Status: "Processed"}/>
                                <ProgressBar variant="info" now={(loanBasicInfo.Status == "Underwriting" || loanBasicInfo.Status == "Approved")?25:0} key={3} label = {loanBasicInfo.Status == "Underwriting"? loanBasicInfo.Status: "Underwritten"}/>
                                <ProgressBar variant="success" now={loanBasicInfo.Status == "Approved"?25:0} key={4} label = {loanBasicInfo.Status == "Approved"? loanBasicInfo.Status: "Approved"}/>
                            </ProgressBar>
                        </Col>)}
                        
                    </Col>
                    {!AdminID? (
                        <Col md={3} lg={3}><Link className="mt-auto" to = {"/Portal/Loans?LoanID="+LoanID}><SubmitButton text = "Edit Loan Info" width = '40'/></Link></Col>
                    ):(
                        <Col md={3} lg={3}>
                            {loanBasicInfo.Status != "Approved" && (<SubmitButton text = "Update Loan Status" onClick = {() => setConfirmStatusUpdate(true)} width = "40" />)}           
                        </Col>)}

                    <Col md={3} lg={3}>
                        <SubmitButton text = "View a Different Loan" onClick = {handleBackToLoanList} width='40'/>
                    </Col>
                    
                </Row>

                {confirmStatusUpdate && (
                    <ConfirmStatusUpdate
                        ClientID = {ClientID}
                        LoanID = {LoanID}
                        setLoanBasicInfo = {setLoanBasicInfo}
                        currentStatus = {loanBasicInfo.Status}
                        updateLoanStatus = {updateLoanStatus}
                        onHide = {() => setConfirmStatusUpdate(false)}
                    />
                )}

                <Container>
                    {Object.keys(loanInfo).map((fieldName, index) => (
                        <Row key = {index}>
                            <Col>
                                <p><strong>{fieldName}:</strong> </p>
                            </Col>
                            <Col>
                                <p>{loanInfo[fieldName]}</p>
                            </Col>
                        </Row>
                        
                    ))}
                </Container>

            </Container>
            )}

            <Container className = 'mx-3' id="files">
                <h2><u><strong>Files</strong></u></h2>

                <Button className="m-2" variant = "light" onClick = {handleDownloadZip}>Download Submitted Files ({filePathList.length})</Button>
                {(clickedLoan)&&(
                    <SubmitButton
                        text = {AdminID?"View Individual Files and Notes" :"View Individual Files and Comments"}
                        onClick = {() => setShowNoteModal(true)} 
                        width = "40"/>
                    )}

                {showNoteModal && (
                    <NoteModal
                        credentials = {credentials}
                        username = {adminUsername}
                        loan = {loanBasicInfo}
                        setLoan = {setLoanBasicInfo}
                        allLoans = {allLoans}
                        setAllLoans = {setAllLoans}
                        allClients = {props.allClients}
                        setAllClients = {props.setAllClients}
                        isFromReview = {true}
                        allLoansR = {loanList}
                        setAllLoansR = {setLoanList}
                        uploadedFiles = {loanFiles}
                        onHide = {() => setShowNoteModal(false)}
                        isAdmin = {AdminID?true:false}
                    />
                )}

                {(AdminID && clickedLoan) && (
                    <SubmitButton text = "Upload a File" onClick = {() => setShowUploadModal(true)} width = "40" />
                )}

                {showUploadModal && (
                    <AdminUploadModal
                        credentials = {credentials}
                        ClientID = {ClientID}
                        loan = {loanBasicInfo}
                        uploadedFiles = {loanFiles}
                        setUploadedFiles = {setLoanFiles}
                        isAdmin = {AdminID?true:false}
                        onHide = {() => setShowUploadModal(false)}
                    />
                )}


                <Container>
                    <Row>
                        <h3>Missing Files:</h3>
                    </Row>
                    {missingFiles.map((fileName, index) => (
                        <Row key = {index}>
                            <p><strong>- {fileName}</strong> </p>
                        </Row> 
                    ))}
                </Container>

                

            </Container>

        </div>
    );
    
};

export default Review;
